export type ErrorCode = string & { __type: 'ErrorCode' };

export type ClientErrorCode =
  // Generic internal errors
  | 'GENERIC'
  | 'NOT_ENOUGH_DATA'
  | 'NOT_WAGER'
  | 'NOT_PAYABLE'
  | 'NOT_FRACTION'
  // Sanity errors
  | 'NOT_ENOUGH_MONEY'
  | 'NOT_AUTHENTICATED'
  // Payment errors
  | 'PAYMENT_REQUEST_FAILURE'
  | 'PAYMENT_FETCH_FAILURE'
  | 'IMPOSSIBLE_ROWS'
  | 'MARKETPLACE_WAGER_ALREADY_PAID'
  // Exclusion errors
  | 'USER_HAS_EXCLUSION'
  | 'PLAYER_PAUSED'
  | 'VERTICAL_EXCLUSION'
  | 'SUSPENSION'
  // Draw errors
  | 'BAD_DRAW'
  | 'BACKEND_ERROR'
  | 'BAD_RESULT'
  | 'BAD_BET'
  // Limit errors
  | 'CUSTOMER_LIMIT'
  | 'TIME_LIMIT'
  | 'AMOUNT_LIMIT'
  | 'DAILY_AMOUNT_LIMIT'
  | 'ROW_LIMIT'
  // Game enhancement errors
  | 'COMPETITION_ERROR'
  // Fraction payment errors
  | 'INVALID_PARAM'
  | 'MEMBER_NOT_FOUND'
  | 'MEMBER_ACTIVE'
  | 'GROUP_ACTIVE'
  | 'ITS_LIMIT_EXCEEDED'
  | 'NO_SHARES_AVAILABLE'
  | 'OPS_INSUFFICIENT_FUNDS'
  | 'OPS_ACCOUNT_NOT_REACHABLE'
  | 'TECHNICAL_ERROR'
  | 'CONTAINER_NOT_FOUND'
  | 'ACTIVATED_DRAW_NOT_FOUND'
  | 'NO_OPS_REPLY'
  | 'NO_ITS_REPLY'
  | 'MAX_SHARES_PER_MEMBER_REACHED'
  // Cancelation errors
  | 'CANCEL_PAYMENT_FAILURE'
  | 'CANCELATION_TIME_EXCEEDED'
  | 'CANCELATION_STOPPED'
  | 'CANCELATION_RETAILER_NOT_POSSIBLE';

export const CLIENT_ERROR_MESSAGES = {
  GENERIC: 'Unknown error',
  NOT_ENOUGH_DATA: 'Not enough data to start payment process',
  NOT_WAGER: 'Instance is not of type Wager',
  NOT_PAYABLE: 'Item is not payable',
  NOT_FRACTION: 'Instance is not of type Fraction',
  NOT_ENOUGH_MONEY: 'Not enough money',
  NOT_AUTHENTICATED: 'Not authenticated',
  PAYMENT_REQUEST_FAILURE: 'Payment request failure',
  PAYMENT_FETCH_FAILURE: 'Payment fetch failure',
  IMPOSSIBLE_ROWS: 'Bet includes impossible rows',
  CANCEL_PAYMENT_FAILURE: 'Cancel wager failure',
  CANCELATION_TIME_EXCEEDED: 'Cancel wager time exceeded',
  CANCELATION_STOPPED: 'Cancel wager stopped on draw',
  CANCELATION_RETAILER_NOT_POSSIBLE: 'Cancel wager for retailer not possible online',
  USER_HAS_EXCLUSION: 'User paused or excluded',
  BAD_DRAW: 'Draw not playable',
  BACKEND_ERROR: 'Error in backend',
  BAD_RESULT: 'Error with result',
  BAD_BET: 'Bet is not playable',
  CUSTOMER_LIMIT: 'Customer exceeded limit',
  TIME_LIMIT: 'Customer exceeded limit',
  PLAYER_PAUSED: 'User paused',
  AMOUNT_LIMIT: 'Exceeded amount limit',
  DAILY_AMOUNT_LIMIT: 'Exceeded amount limit',
  ROW_LIMIT: 'Exceeded row limit',
  SUSPENSION: 'User suspension',
  VERTICAL_EXCLUSION: 'User NAR paused',
  COMPETITION_ERROR: 'Error with competition',
  INVALID_PARAM: 'Invalid parameter',
  MEMBER_NOT_FOUND: 'Member not found',
  MEMBER_ACTIVE: 'Member is active',
  GROUP_ACTIVE: 'Group is active',
  ITS_LIMIT_EXCEEDED: 'ITS limit exceeded',
  NO_SHARES_AVAILABLE: 'No shares available',
  OPS_INSUFFICIENT_FUNDS: 'OPS insufficient funds',
  OPS_ACCOUNT_NOT_REACHABLE: 'OPS account not reachable',
  TECHNICAL_ERROR: 'Technical error',
  CONTAINER_NOT_FOUND: 'Container not found',
  ACTIVATED_DRAW_NOT_FOUND: 'Activated draw not found',
  NO_OPS_REPLY: 'No OPS reply',
  NO_ITS_REPLY: 'No ITS reply',
  MAX_SHARES_PER_MEMBER_REACHED: 'Max shares per member reached',
  MARKETPLACE_WAGER_ALREADY_PAID: 'Marketplace retailer wager already paid',
} as const satisfies Record<ClientErrorCode, string>;

/**
 * Function to check if the provided error code is a client error.
 *
 * @param code The error code to check.
 * @returns true if the error code is a client error, false otherwise.
 */
export function isClientError(code: unknown): code is ClientErrorCode {
  if (typeof code !== 'string') {
    return false;
  }
  return code in CLIENT_ERROR_MESSAGES;
}

export type MiddlewareErrorCode =
  | 'GENERIC'
  | 'PAYMENT_ABORTED'
  | 'PAYMENT_ABORTED_MP_SHARES'
  | 'NOT_ENOUGH_MONEY'
  | 'NOT_AUTHENTICATED'
  | 'USER_HAS_EXCLUSION'
  | 'TIME_LIMIT'
  | 'GRASSROOT'
  | 'NOT_WAGER'
  | 'NOT_FRACTION'
  | 'GROUP_FETCH_ERROR'
  | 'GROUP_JOIN_ERROR'
  | 'CANCEL_ABORTED';

export const MIDDLEWARE_ERROR_MESSAGES = {
  GENERIC: 'Unknown error',
  PAYMENT_ABORTED: 'Customer aborted payment',
  PAYMENT_ABORTED_MP_SHARES: 'Customer aborted payment, new shares bought for MP',
  NOT_ENOUGH_MONEY: 'Not enough money',
  NOT_AUTHENTICATED: 'Not authenticated',
  USER_HAS_EXCLUSION: 'User paused or excluded',
  TIME_LIMIT: 'Time limit exceeded',
  GRASSROOT: 'Failed to fetch sponsorships',
  NOT_WAGER: 'Instance is not wager',
  NOT_FRACTION: 'Instance is not fraction',
  GROUP_FETCH_ERROR: 'Failed to fetch group',
  GROUP_JOIN_ERROR: 'Failed to join group',
  CANCEL_ABORTED: 'Customer aborted cancelation',
} as const satisfies Record<MiddlewareErrorCode, string>;

/**
 * Function to check if the provided error code is a middleware error.
 *
 * @param code The error code to check.
 * @returns true if the error code is a middleware error, false otherwise.
 */
export function isMiddlewareError(code: unknown): code is MiddlewareErrorCode {
  if (typeof code !== 'string') {
    return false;
  }
  return code in MIDDLEWARE_ERROR_MESSAGES;
}
